<template>
  <div
    v-if="open"
    class="signup-backdrop download-bd open"
    @mousedown="onBackdropMouseDown"
  >
    <div
      ref="modal"
      class="signup-modal download"
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-title"
    >
      <div class="sheet-handle" aria-hidden="true" />
      <button
        type="button"
        class="signup-close"
        aria-label="Schließen"
        @click="close"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M6 6 L18 18 M18 6 L6 18" />
        </svg>
      </button>
      <div class="signup-modal-body">
        <div class="dl-mark dl-stagger dl-stagger-1">
          <span class="dl-mark-float">
            <VibelaMark :size="56" />
          </span>
        </div>
        <div class="dl-eyebrow dl-stagger dl-stagger-2">Lass loslegen</div>
        <h2 id="download-title" class="dl-h2 dl-stagger dl-stagger-2">
          Hol dir die <em class="grad-text italic">App</em>.
        </h2>
        <p class="dl-sub dl-stagger dl-stagger-2">
          Verfügbar im App Store und bei Google Play — installier sie und entdecke Profile in deiner Stadt.
        </p>
        <div class="dl-section-title dl-stagger dl-stagger-3 mt-8">
          So geht's los
        </div>
        <div
          class="dl-badges dl-stagger dl-stagger-5"
          :class="{ 'two-up': platform === 'desktop' }"
        >
          <a
            v-if="platform === 'desktop' || platform === 'ios'"
            :href="appStoreUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="dl-badge"
            aria-label="Vibela im App Store herunterladen"
            @click="onStoreClick('apple')"
          >
            <span class="badge-glyph">
              <svg width="22" height="24" viewBox="0 0 24 24" fill="#FFF4E6" aria-hidden="true">
                <path
                  d="M17.3 12.7c0-2.4 1.9-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.9-1.5-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.4 1.2 0 1.6-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.1-1.1 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7 0 0-2.5-.9-2.7-3.8zM15 5.4c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 .1 2-.5 2.7-1.3z"
                />
              </svg>
            </span>
            <span class="badge-text">
              <span class="badge-small">Lade im</span>
              <span class="badge-big">App Store</span>
            </span>
          </a>
          <a
            v-if="platform === 'desktop' || platform === 'android'"
            :href="playStoreUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="dl-badge"
            aria-label="Vibela bei Google Play herunterladen"
            @click="onStoreClick('google')"
          >
            <span class="badge-glyph">
              <svg width="22" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <defs>
                  <linearGradient :id="playBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#67D5FF" />
                    <stop offset="100%" stop-color="#1E88E5" />
                  </linearGradient>
                  <linearGradient :id="playGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#00E676" />
                    <stop offset="100%" stop-color="#00B884" />
                  </linearGradient>
                  <linearGradient :id="playYellow" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#FFE94B" />
                    <stop offset="100%" stop-color="#FFB300" />
                  </linearGradient>
                  <linearGradient :id="playRed" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#FF6B6B" />
                    <stop offset="100%" stop-color="#E63946" />
                  </linearGradient>
                </defs>
                <path d="M4 2.5 L13 12 L4 21.5 Z" :fill="`url(#${playBlue})`" />
                <path d="M4 2.5 L17 10 L13 12 Z" :fill="`url(#${playGreen})`" />
                <path d="M17 10 L21 12 L17 14 Z" :fill="`url(#${playYellow})`" />
                <path d="M4 21.5 L17 14 L13 12 Z" :fill="`url(#${playRed})`" />
              </svg>
            </span>
            <span class="badge-text">
              <span class="badge-small">Jetzt bei</span>
              <span class="badge-big">Google Play</span>
            </span>
          </a>
        </div>
        <div v-if="showQr" class="dl-qr-block dl-stagger dl-stagger-6">
          <div class="dl-qr-vis">
            <StyledQRCode :data="smartLink" :size="92" />
          </div>
          <div class="dl-qr-text">
            <div class="qr-title">Scan mit deinem Handy</div>
            <div class="qr-sub">
              Smart-Link erkennt dein Gerät automatisch und führt dich zum Store.
            </div>
          </div>
        </div>
        <div class="dl-fine dl-stagger dl-stagger-6">
          Mit der Anmeldung stimmst du unseren
          <a href="/agb">AGB</a>
          und der
          <a href="/datenschutz">Datenschutzerklärung</a>
          zu.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VibelaMark from './VibelaMark.vue';
import StyledQRCode from './StyledQRCode.vue';
import {
  buildStoreUrls,
  isQrViewport,
  resolveCtaMeta,
  trackEvent,
} from '../analytics/ga4';

const DEFAULT_CTA = {
  cta_location: 'header_start',
  cta_intent: 'register',
};

export default {
  name: 'RegisterModal',
  components: { VibelaMark, StyledQRCode },
  data() {
    const urls = buildStoreUrls(DEFAULT_CTA.cta_location, DEFAULT_CTA.cta_intent);
    return {
      open: false,
      platform: 'desktop',
      showQr: false,
      triggerEl: null,
      bodyOverflow: '',
      openedAt: 0,
      ctaLocation: DEFAULT_CTA.cta_location,
      ctaIntent: DEFAULT_CTA.cta_intent,
      smartLink: urls.smartLink,
      appStoreUrl: urls.appStoreUrl,
      playStoreUrl: urls.playStoreUrl,
      playBlue: `register-play-blue-${this._uid}`,
      playGreen: `register-play-green-${this._uid}`,
      playYellow: `register-play-yellow-${this._uid}`,
      playRed: `register-play-red-${this._uid}`,
    };
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.lockScroll();
        this.$nextTick(() => {
          this.scrollBackdropTop();
          document.addEventListener('keydown', this.onKeyDown);
          setTimeout(() => {
            const closeBtn = this.$refs.modal?.querySelector('button.signup-close');
            closeBtn?.focus({ preventScroll: true });
          }, 350);
        });
      } else {
        this.unlockScroll();
        document.removeEventListener('keydown', this.onKeyDown);
        setTimeout(() => {
          if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
            this.triggerEl.focus();
          }
        }, 200);
      }
    },
  },
  created() {
    this.detectPlatform();
    window.__openRegister = (trigger) => this.openModal(trigger);
    this.syncFromHash();
    window.addEventListener('hashchange', this.syncFromHash);
    document.addEventListener('click', this.onRegisterTriggerClick, true);
  },
  beforeDestroy() {
    delete window.__openRegister;
    window.removeEventListener('hashchange', this.syncFromHash);
    document.removeEventListener('click', this.onRegisterTriggerClick, true);
    document.removeEventListener('keydown', this.onKeyDown);
    this.unlockScroll();
  },
  methods: {
    detectPlatform() {
      const params = new URLSearchParams(window.location.search);
      const override =
        params.get('platform') || localStorage.getItem('vibela_platform_override');
      if (override === 'ios' || override === 'android' || override === 'desktop') {
        this.platform = override;
      } else {
        const ua = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(ua)) {
          this.platform = 'ios';
        } else if (/android/.test(ua)) {
          this.platform = 'android';
        } else {
          this.platform = 'desktop';
        }
      }
      this.showQr = isQrViewport();
    },
    applyCtaMeta(meta) {
      this.ctaLocation = meta.cta_location;
      this.ctaIntent = meta.cta_intent;
      const urls = buildStoreUrls(meta.cta_location, meta.cta_intent);
      this.appStoreUrl = urls.appStoreUrl;
      this.playStoreUrl = urls.playStoreUrl;
      this.smartLink = urls.smartLink;
    },
    syncFromHash() {
      if (window.location.hash === '#start') {
        if (!this.open) {
          this.openModal(null, DEFAULT_CTA);
        }
      } else if (this.open) {
        this.close({ skipHash: true, skipTrack: false });
      }
    },
    openModal(trigger, defaults = DEFAULT_CTA) {
      const meta = resolveCtaMeta(trigger, defaults);
      this.applyCtaMeta(meta);
      this.triggerEl = trigger || null;
      this.showQr = isQrViewport();
      this.openedAt = Date.now();
      if (window.location.hash !== '#start') {
        history.pushState(null, '', '#start');
      }
      const wasOpen = this.open;
      this.open = true;
      if (!wasOpen) {
        trackEvent('app_modal_open', {
          cta_location: meta.cta_location,
          cta_intent: meta.cta_intent,
        });
        if (this.showQr) {
          trackEvent('app_qr_view', { cta_location: meta.cta_location });
        }
      }
    },
    close(options = {}) {
      if (!this.open) {
        return;
      }
      if (!options.skipTrack) {
        const timeOpenSec = this.openedAt
          ? Math.max(0, Math.round((Date.now() - this.openedAt) / 1000))
          : 0;
        trackEvent('app_modal_close', {
          cta_location: this.ctaLocation,
          time_open_sec: timeOpenSec,
        });
      }
      this.open = false;
      if (!options.skipHash && window.location.hash === '#start') {
        history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
    },
    onStoreClick(store) {
      trackEvent('app_store_click', {
        store,
        cta_location: this.ctaLocation,
        cta_intent: this.ctaIntent,
      });
    },
    onBackdropMouseDown(event) {
      if (event.target === event.currentTarget) {
        this.close();
      }
    },
    onRegisterTriggerClick(event) {
      const link = event.target.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href') || '';
        if (this.isRegisterHref(href)) {
          event.preventDefault();
          this.openModal(link, this.defaultsForTrigger(link));
          return;
        }
      }
      const registerBtn = event.target.closest(
        '.hero-cta .btn-primary, .finale .btn-cream, .nav-cta[href="#start"]'
      );
      if (registerBtn) {
        event.preventDefault();
        this.openModal(registerBtn, this.defaultsForTrigger(registerBtn));
      }
    },
    defaultsForTrigger(el) {
      if (el.closest('.hero-cta')) {
        return { cta_location: 'hero', cta_intent: 'app' };
      }
      if (el.closest('.finale')) {
        return { cta_location: 'bottom_cta', cta_intent: 'app' };
      }
      if (el.closest('.footer')) {
        return { cta_location: 'footer_link', cta_intent: 'register' };
      }
      if (el.classList.contains('nav-cta') || el.closest('.nav-right')) {
        return { cta_location: 'header_start', cta_intent: 'register' };
      }
      return DEFAULT_CTA;
    },
    isRegisterHref(href) {
      if (!href) {
        return false;
      }
      try {
        const url = new URL(href, window.location.href);
        return url.hash === '#start';
      } catch (e) {
        return href === '#start' || href.endsWith('#start');
      }
    },
    lockScroll() {
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },
    unlockScroll() {
      document.body.style.overflow = this.bodyOverflow;
    },
    scrollBackdropTop() {
      requestAnimationFrame(() => {
        const backdrop = document.querySelector('.signup-backdrop.download-bd');
        if (backdrop) {
          backdrop.scrollTop = 0;
        }
      });
    },
    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.$refs.modal) {
        return;
      }
      const focusable = Array.from(
        this.$refs.modal.querySelectorAll(
          'a, button, input, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>
